import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';

const AppointmentManager = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);

    // Filters
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedDoctorFilter, setSelectedDoctorFilter] = useState('');

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [formData, setFormData] = useState({
        patientId: '',
        guestName: '',
        guestPhone: '',
        doctorId: '',
        date: new Date().toISOString().split('T')[0],
        timeSlot: '',
        type: 'Physical',
        notes: ''
    });

    useEffect(() => {
        fetchDoctors();
        fetchPatients();
    }, []);

    useEffect(() => {
        fetchAppointments();
    }, [selectedDate, selectedDoctorFilter]);

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            let query = `?date=${selectedDate}`;
            if (selectedDoctorFilter) query += `&doctorId=${selectedDoctorFilter}`;

            const response = await api.get(`/clinic/appointments${query}`);
            setAppointments(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            setLoading(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await api.get('/clinic/staff');
            setDoctors(response.data.filter(u => u.role === 'Doctor'));
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    const fetchPatients = async () => {
        try {
            const response = await api.get('/clinic/patients');
            setPatients(response.data);
        } catch (error) {
            console.error('Error fetching patients:', error);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/clinic/appointments', formData);
            Swal.fire('Success', 'Appointment booked successfully', 'success');
            setShowModal(false);
            fetchAppointments();
            // Reset Form partially
            setFormData(prev => ({ ...prev, timeSlot: '', notes: '', guestName: '', guestPhone: '' }));
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Booking failed', 'error');
        }
    };

    const handleCancel = async (id) => {
        const result = await Swal.fire({
            title: 'Cancel Appointment?',
            text: "Status will be updated to Cancelled.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, cancel it'
        });

        if (result.isConfirmed) {
            try {
                await api.put(`/clinic/appointments/${id}`, { status: 'Cancelled' });
                Swal.fire('Cancelled', 'Appointment has been cancelled.', 'success');
                fetchAppointments();
            } catch (error) {
                Swal.fire('Error', 'Failed to cancel appointment', 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Upcoming': return 'bg-primary-50 text-primary-600';
            case 'Completed': return 'bg-success-50 text-success-600';
            case 'Cancelled': return 'bg-danger-50 text-danger-600';
            case 'Missed': return 'bg-warning-50 text-warning-600';
            default: return 'bg-light text-secondary';
        }
    };

    // Helper to generate time slots (09:00 to 18:00 for demo)
    const generateTimeSlots = () => {
        const slots = [];
        for (let i = 9; i < 18; i++) {
            slots.push(`${i < 10 ? '0' : ''}${i}:00`);
            slots.push(`${i < 10 ? '0' : ''}${i}:30`);
        }
        return slots;
    };

    return (
        <div className="card h-100 p-0 radius-12 fade-in-up">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-40-px h-40-px radius-8 bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-xl">
                            <Icon icon="solar:calendar-mark-bold" />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">Appointments</h5>
                            <p className="text-sm text-secondary mb-0">Manage bookings & schedules</p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="shadow-sm border-secondary-light radius-8"
                            style={{ width: 'auto' }}
                        />
                        <Form.Select
                            value={selectedDoctorFilter}
                            onChange={(e) => setSelectedDoctorFilter(e.target.value)}
                            className="shadow-sm border-secondary-light radius-8"
                            style={{ width: 'auto', minWidth: '150px' }}
                        >
                            <option value="">All Doctors</option>
                            {doctors.map(doc => <option key={doc._id} value={doc._id}>{doc.name}</option>)}
                        </Form.Select>
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn btn-primary px-20 py-10 radius-8 d-flex align-items-center gap-2 shadow-sm hover-scale"
                        >
                            <Icon icon="solar:calendar-add-bold" className="text-xl" />
                            Book Appointment
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th>Time / Type</th>
                                <th>Patient Details</th>
                                <th>Doctor</th>
                                <th>Status</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : appointments.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <div className="d-flex flex-column align-items-center">
                                            <Icon icon="solar:calendar-minimalistic-broken" className="text-4xl text-secondary-light mb-2" />
                                            <span className="text-secondary fw-medium">No appointments for this date.</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                appointments.map(apt => (
                                    <tr key={apt._id} className="hover-bg-light transition-all">
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="fw-bold text-dark text-lg">{apt.timeSlot}</span>
                                                <span className="text-xs text-secondary d-flex align-items-center gap-1">
                                                    <Icon icon={apt.type === 'Online' ? "solar:videocamera-bold" : "solar:hospital-bold"} /> {apt.type}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden bg-info-50 d-flex justify-content-center align-items-center text-info-600 fw-bold">
                                                    {apt.patientId?.profileImage ? <img src={apt.patientId.profileImage} alt="" className="w-100 h-100 object-fit-cover" /> : (apt.guestName ? apt.guestName.charAt(0) : (apt.patientId?.name.charAt(0) || '?'))}
                                                </div>
                                                <div>
                                                    <span className="fw-medium d-block text-dark">{apt.patientId ? apt.patientId.name : `${apt.guestName} (Guest)`}</span>
                                                    <span className="text-xs text-secondary">{apt.patientId ? apt.patientId.phone : apt.guestPhone}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-sm fw-medium text-dark">{apt.doctorId?.name}</span>
                                            <span className="d-block text-xs text-secondary">{apt.doctorId?.specialization}</span>
                                        </td>
                                        <td>
                                            <span className={`px-12 py-6 radius-4 text-xs fw-bold ${getStatusBadge(apt.status)}`}>
                                                {apt.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {apt.status === 'Upcoming' ? (
                                                <button
                                                    onClick={() => handleCancel(apt._id)}
                                                    className="btn btn-outline-danger btn-sm px-12 radius-6 fw-medium d-inline-flex align-items-center gap-1"
                                                >
                                                    <Icon icon="solar:forbidden-circle-bold" /> Cancel
                                                </button>
                                            ) : (
                                                <span className="text-secondary text-sm">No action available</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* BOOKING MODAL */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Book New Appointment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={12} className="mb-3">
                                <div className="d-flex gap-4">
                                    <Form.Check
                                        type="radio"
                                        label="Registered Patient"
                                        name="patientType"
                                        checked={!isGuest}
                                        onChange={() => setIsGuest(false)}
                                    />
                                    <Form.Check
                                        type="radio"
                                        label="Walk-in Guest"
                                        name="patientType"
                                        checked={isGuest}
                                        onChange={() => setIsGuest(true)}
                                    />
                                </div>
                            </Col>
                        </Row>

                        {!isGuest ? (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Select Patient <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="patientId"
                                    value={formData.patientId}
                                    onChange={handleInputChange}
                                    required={!isGuest}
                                >
                                    <option value="">-- Choose Patient --</option>
                                    {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
                                </Form.Select>
                            </Form.Group>
                        ) : (
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Guest Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="text" name="guestName" value={formData.guestName} onChange={handleInputChange} required={isGuest} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Guest Phone</Form.Label>
                                        <Form.Control type="text" name="guestPhone" value={formData.guestPhone} onChange={handleInputChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <hr className="my-4" />

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Doctor <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="doctorId" value={formData.doctorId} onChange={handleInputChange} required>
                                        <option value="">-- Select Doctor --</option>
                                        {doctors.map(d => <option key={d._id} value={d._id}>{d.name} ({d.specialization})</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Date <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="date" name="date" value={formData.date} onChange={handleInputChange} required />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Time Slot <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="timeSlot" value={formData.timeSlot} onChange={handleInputChange} required>
                                        <option value="">-- Select Time --</option>
                                        {generateTimeSlots().map(slot => <option key={slot} value={slot}>{slot}</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Type</Form.Label>
                                    <Form.Select name="type" value={formData.type} onChange={handleInputChange}>
                                        <option value="Physical">Physical Visit</option>
                                        <option value="Online">Online Consultation</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold">Notes</Form.Label>
                            <Form.Control as="textarea" rows={2} name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Reason for visit..." />
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-3">
                            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit" className="px-4">Confirm Booking</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AppointmentManager;
